import type { SchoolLevel, SubjectSlug, ExerciseDifficulty } from '@jeraisonne/db';
import type { ObjectiveCardsProgress } from '@jeraisonne/db/objectives';

export type ObjectiveType =
  | 'full_program'
  | 'chapter_review'
  | 'current_chapter'
  | 'weak_spots'
  | 'warmup'
  | 'diagnostic'
  | 'brevet_revision'
  | 'brevet_blanc'
  | 'bac_revision'
  | 'bac_blanc';

export interface AppObjectiveTemplate {
  id: string;
  title: string;
  description: string;
  type: ObjectiveType | string;
  allowedLevels: SchoolLevel[];
  subject: SubjectSlug | null;
  filters: Record<string, unknown>;
  /** L'élève choisit un pack de son programme (migration 0514, ex-chapitre). */
  requiresPackSelection: boolean;
  contentType: 'exercises' | 'epreuves';
  isActive: boolean;
  displayOrder: number;
  illustrationUrl?: string | null;
  /** Deadline par défaut (ISO 8601). Pré-remplie sur les objectifs élève. */
  defaultDeadline?: string | null;
}

export interface AppStudentObjective {
  id: string;
  studentId: string;
  template: AppObjectiveTemplate;
  selectedSubject: SubjectSlug | null;
  /** Historique (avant 0514) : n'est plus écrit par l'app. */
  selectedChapterId: string | null;
  /** Pack choisi par l'élève (objectifs « Réviser un pack »). */
  selectedPackId: string | null;
  customFilters: Record<string, unknown>;
  isActive: boolean;
  /** Deadline réelle de l'objectif (ISO 8601, modifiable par l'élève). */
  deadline: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ObjectiveProgress {
  objectiveId: string;
  templateType: string;
  totalExercises: number;
  completedExercises: number;
  completionPercent: number;
  masteryPercent: number;
  /**
   * `cards` : objectif relié aux SKILLCARDS (migration 0514) — complétion = cartes
   * maîtrisées, maîtrise = moyenne ; `exercises` : diagnostic ; `epreuves`.
   */
  kind?: 'cards' | 'exercises' | 'epreuves';
  /** Objectifs SKILLCARDS : cartes du périmètre et leur progression. */
  cards?: ObjectiveCardsProgress;
  /** Objectifs SKILLCARDS : titres des packs du périmètre. */
  packTitles?: string[];
  /** Renseignés uniquement pour les objectifs `contentType === 'epreuves'`. */
  targetEpreuveCount?: number;
  engagedEpreuveCount?: number;
}

export const EPREUVE_TARGET_MIN = 1;
export const EPREUVE_TARGET_MAX = 10;
export const EPREUVE_TARGET_DEFAULT = 3;

export function getEpreuveTargetCount(filters: Record<string, unknown>): number {
  const raw = filters['target_epreuve_count'];
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return EPREUVE_TARGET_DEFAULT;
  return Math.min(EPREUVE_TARGET_MAX, Math.max(EPREUVE_TARGET_MIN, Math.round(n)));
}

export type ExerciseAccessReason =
  | 'subscription'
  | 'daily'
  | 'class_assignment'
  | 'tutor_assignment'
  | 'pass'
  /** Exercice fondamental : toujours gratuit (banque de questions Warmup). */
  | 'fundamental'
  /** Exercice diagnostic : toujours gratuit (QCM de cours, objectif Diagnostic). */
  | 'diagnostic';

export type ExerciseAccessState =
  | { allowed: true; reason: ExerciseAccessReason; expiresAt?: string }
  | { allowed: false; reason: 'no_access' };

export interface DailyExerciseResult {
  exerciseId: string;
  title: string;
  difficulty: ExerciseDifficulty;
  estimatedMinutes: number;
  chapterId: string;
  chapterTitle: string;
  subject: SubjectSlug;
  source: 'scheduled' | 'fallback';
  illustrationUrl: string | null;
}

export interface ClassAssignmentSummary {
  classId: string;
  className: string;
  tutorName: string;
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  assignments: AssignmentItem[];
}

export interface TutorAssignmentSummary {
  tutorId: string;
  tutorName: string;
  tutorAvatarUrl: string | null;
  /** Matière(s) couvertes par ce lien (`tutor_links.subjects`) — vide = toutes matières. */
  subjects: SubjectSlug[];
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  assignments: AssignmentItem[];
}

export interface AssignmentItem {
  id: string;
  exerciseId: string;
  exerciseTitle: string;
  difficulty: ExerciseDifficulty;
  estimatedMinutes: number;
  status: 'pending' | 'in_progress' | 'completed';
  dueAt: string | null;
  /** Date du devoir — sert à savoir s'il a été fait depuis (tentative terminée). */
  assignedAt?: string | null;
}

export interface AppEpreuve {
  id: string;
  title: string;
  description: string | null;
  subject: SubjectSlug | null;
  level: SchoolLevel;
  examType: string | null;
  examYear: number | null;
  sourceFileUrl: string | null;
  ocr_status: 'pending' | 'processing' | 'done' | 'failed';
  status: 'draft' | 'published' | 'archived';
  exercises?: AppEpreuveExercise[];
}

export interface AppEpreuveExercise {
  epreuveId: string;
  exerciseId: string;
  displayOrder: number;
  exercise: {
    id: string;
    title: string;
    difficulty: ExerciseDifficulty;
    estimatedMinutes: number;
  };
}
